import React, { useMemo } from 'react';
import { GridColDef, GridRowModel } from '@mui/x-data-grid';
import { useTranslate } from '@im/react-localization';
import {
  LoadingOverlay,
  NoRowsOverlay,
  SortedAscendingIcon,
  SortedDescendingIcon,
} from 'src/components/DataGrid/DataGridFunctions';
import { DataGrid } from 'src/components/DataGrid';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import BidJSON from './BidJSON';
import { BidRowList, useBLCBidDetails } from '../hooks/useBLCHooks';
import { useSearchParams } from 'react-router-dom';
import {
  useParserReprocessBids,
  useParserReprocessBidsAudit,
} from '../hooks/useParserReprocess';
import { useDateTimeFormatter } from 'src/hooks/dateTimeFormatter';
import { LoadingButton } from '@mui/lab';
import useSnackBar from 'src/hooks/useSnackBar';
import { DownloadMultipleFiles } from 'src/components/Download/DownloadMultipleFiles';
import { useAccount } from '@azure/msal-react';

interface DataColumn {
  field: string;
  headerName: string;
  type?: string;
}

export default function BidDetails(props: Readonly<{ row: BidRowList }>) {
  const translate = useTranslate();
  const formatDateTime = useDateTimeFormatter();
  const [searchParams] = useSearchParams();
  const { showSnackBar } = useSnackBar();
  const countryCode = searchParams.get('countryCode');
  const companyCode = searchParams.get('companyCode') ?? '';

  const bidDetails = useBLCBidDetails({
    bidEventHeaderId: props.row.bidEventHeaderId,
    customerSetId: props.row.customerSetId,
    id: props.row.id,
  });
  const parserHeaderID =
    bidDetails.data?.value.responseDetails[0].parserHeaderID;
  const dataColumns: DataColumn[] = [
    { field: 'source', headerName: translate('BidLifeCycle.ColumnSource') },
    { field: 'summary', headerName: translate('BidLifeCycle.ColumnSummary') },
    { field: 'status', headerName: translate('BidLifeCycle.ColumnStatus') },
    {
      field: 'statusDateTimeUTC',
      headerName: translate('BidLifeCycle.ColumnStatusDatetime'),
      type: 'dateTime',
    },
    { field: 'notes', headerName: translate('BidLifeCycle.ColumnNotes') },
  ];
  const columns: GridColDef[] = dataColumns.map((column) => {
    return {
      field: column.field,
      flex: 2,
      headerName: column.headerName,
      renderCell: (item) => {
        if (column.type === 'dateTime') return formatDateTime(item.value, true);
        else return item.value;
      },
      sortable: false,
    };
  });

  const reprocessBids = useParserReprocessBids({
    countryCode: countryCode ?? '',
    documentIds: [`${parserHeaderID}~${props.row.bidNumber}`],
  });
  const reprocessBidsAudit = useParserReprocessBidsAudit({
    BusinessEntity: '',
    CompanyCode: companyCode ?? '',
    CountryCode: countryCode ?? '',
    DealNumber: props.row.bidNumber,
    Username: useAccount()?.username.toLowerCase() ?? '',
    VendorName: props.row.vendorName,
    VendorProgramName: props.row.vendorProgramName,
  });
  const handleReprocessBids = () => {
    // Call Audit API first
    reprocessBidsAudit.mutateAsync().catch(() => {
      showSnackBar({
        autoHide: 3000,
        message: translate('BidLifeCycle.ReprocessFailedMessage'),
        severity: 'error',
      });
    });
    reprocessBids
      .mutateAsync()
      .then(() => {
        showSnackBar({
          autoHide: 3000,
          message: translate('BidLifeCycle.ReprocessSuccess'),
          severity: 'success',
        });
      })
      .catch(() => {
        showSnackBar({
          autoHide: 3000,
          message: translate('BidLifeCycle.ReprocessFailedMessage'),
          severity: 'error',
        });
      });
  };
  const disabledButton = useMemo(
    () =>
      !parserHeaderID ||
      [
        'Deactivated',
        'COG Request New',
        'COG Request Assigned',
        'COG Request Modified',
        'COG Request On Hold',
        'COG Request 48hr Drop',
        'COG Request Approved',
        'COG Request Denied',
      ].includes(bidDetails.data?.value.responseDetails[0].status ?? ''),
    [bidDetails.data?.value.responseDetails, parserHeaderID]
  );

  return (
    <Box p={1}>
      <Stack direction={'row'} gap={1} mb={2} mt={1}>
        <BidJSON disable={disabledButton} parserHeaderID={parserHeaderID} />
        <LoadingButton
          disabled={disabledButton}
          loading={reprocessBids.isLoading || reprocessBidsAudit.isLoading}
          loadingPosition="start"
          onClick={() => handleReprocessBids()}
          startIcon={<AutorenewIcon />}
        >
          {translate('BidLifeCycle.ReprocessBids')}
        </LoadingButton>
        <DownloadMultipleFiles
          headerId={Number(parserHeaderID)}
          label={translate('BidLifeCycle.DownloadPaperWork')}
        />
      </Stack>
      <DataGrid
        autoHeight
        columnBuffer={columns.length}
        columns={columns}
        components={{
          ColumnSortedAscendingIcon: SortedAscendingIcon,
          ColumnSortedDescendingIcon: SortedDescendingIcon,
          LoadingOverlay: LoadingOverlay,
          NoRowsOverlay: NoRowsOverlay,
        }}
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableSelectionOnClick
        getRowHeight={(params) => 'auto'}
        getRowId={(row: GridRowModel) => row.id}
        hideFooterPagination
        hideFooterSelectedRowCount
        loading={bidDetails.isLoading}
        rows={bidDetails.data?.value.responseDetails ?? []}
        sx={{
          '.MuiDataGrid-cell': {
            outline: 'transparent !important',
            overflow: 'hidden',
            padding: '0 20px',
          },
          '.MuiDataGrid-columnHeader--moving': {
            backgroundColor: 'transparent',
          },
          '.MuiDataGrid-columnHeaderCheckbox': {
            padding: '0px',
          },
          '.MuiDataGrid-columnHeaderTitle': {
            whiteSpace: 'normal',
          },
          '.MuiDataGrid-footerContainer>div': {
            flexWrap: 'wrap',
          },
          '.MuiDataGrid-footerContainer>div>div': {
            flex: 1,
          },
          '.MuiDataGrid-row': {
            minHeight: '52px !important',
          },
        }}
      />
    </Box>
  );
}
